import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Bell, Megaphone, BookOpen, Wallet, GraduationCap, Home, ShieldAlert,
  Sparkles, Wrench, Crown, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  megaphone: Megaphone, "book-open": BookOpen, wallet: Wallet,
  "graduation-cap": GraduationCap, home: Home, "shield-alert": ShieldAlert,
  sparkles: Sparkles, wrench: Wrench, crown: Crown,
};

export default function NotificationPreferences() {
  const [meta, setMeta] = useState(null);
  const [prefs, setPrefs] = useState({});
  const [pushGlobal, setPushGlobal] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, p] = await Promise.all([
          api.get("/notifications/meta"),
          api.get("/notifications/preferences"),
        ]);
        setMeta(m.data);
        setPrefs(p.data.preferences || {});
        setPushGlobal(p.data.push_notifications_enabled !== false);
      } catch {
        toast.error("Impossible de charger les préférences");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const persist = async (nextPrefs, nextPushGlobal) => {
    setSaving(true);
    try {
      await api.put("/notifications/preferences", {
        preferences: nextPrefs,
        push_notifications_enabled: nextPushGlobal,
      });
    } catch {
      toast.error("Sauvegarde impossible — réessaie");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    persist(next, pushGlobal);
  };

  const toggleGlobal = () => {
    const next = !pushGlobal;
    setPushGlobal(next);
    persist(prefs, next);
  };

  if (loading) {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="notif-prefs-card">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Loader2 size={14} className="animate-spin" /> Chargement des préférences…
        </div>
      </section>
    );
  }

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-6"
      data-testid="notif-prefs-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
          <Bell size={18} />
        </div>
        <div className="flex-1">
          <p className="font-medium text-slate-900">Préférences de notifications</p>
          <p className="text-sm text-slate-500">
            Choisis les catégories que tu veux recevoir. Les alertes urgentes de maintenance restent
            toujours envoyées, pour ta sécurité.
          </p>
        </div>
      </div>

      {/* Master switch */}
      <div
        className="mt-5 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
        data-testid="notif-prefs-master"
      >
        <div className="min-w-0">
          <p className="font-medium text-slate-900 text-sm">Notifications push activées</p>
          <p className="text-xs text-slate-500">
            Coupe tout d'un coup. On te préviendra quand même en cas d'incident critique.
          </p>
        </div>
        <Switch
          checked={pushGlobal}
          onCheckedChange={toggleGlobal}
          data-testid="notif-prefs-master-switch"
        />
      </div>

      {/* Per-category */}
      <ul className="mt-5 divide-y divide-slate-100" data-testid="notif-prefs-list">
        {(meta?.categories || []).map((c) => {
          const Icon = ICONS[c.icon] || Bell;
          const on = prefs[c.key] ?? c.default_opt_in;
          return (
            <li
              key={c.key}
              className={`py-3 flex items-center gap-3 ${!pushGlobal ? "opacity-50 pointer-events-none" : ""}`}
              data-testid={`notif-pref-item-${c.key}`}
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900">{c.label}</p>
              </div>
              <Switch
                checked={!!on}
                onCheckedChange={() => toggleCategory(c.key)}
                data-testid={`notif-pref-switch-${c.key}`}
              />
            </li>
          );
        })}
      </ul>

      {saving && (
        <p className="mt-3 text-xs text-slate-400 flex items-center gap-1">
          <Loader2 size={11} className="animate-spin" /> Sauvegarde…
        </p>
      )}
    </section>
  );
}
